import { useRef, type KeyboardEvent } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';
import { colorName } from '../categories/categoryPalette';

/**
 * A category's colour: a radio group of round swatches, one per colour on offer.
 *
 * The keyboard model is RadioRows': arrow keys move the choice and the focus together, wrapping at the
 * ends, and only the chosen swatch is in the Tab order. Each swatch is named by its colour ("Orange")
 * and the chosen one carries a ring and a check, so the choice never rests on colour alone.
 *
 * The colours are content (the shared palette), drawn by inline style. A swatch opts out of forced
 * colours, which would otherwise paint every swatch the same system colour and drop the ring.
 */
export function SwatchPicker({
  colors,
  value,
  onChange,
  labelledBy,
}: {
  /** The colours on offer, in palette order. */
  colors: readonly string[];
  value: string;
  onChange: (color: string) => void;
  /** The id of the visible label ("Color"). */
  labelledBy: string;
}) {
  const swatches = useRef(new Map<string, HTMLButtonElement>());
  const at = colors.indexOf(value);
  // The Tab stop: the chosen swatch, or the first if the choice is not on offer.
  const stop = at === -1 ? colors[0] : value;

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const delta =
      e.key === 'ArrowDown' || e.key === 'ArrowRight'
        ? 1
        : e.key === 'ArrowUp' || e.key === 'ArrowLeft'
          ? -1
          : 0;
    if (delta === 0 || colors.length === 0) return;
    e.preventDefault();
    const next = colors[(Math.max(at, 0) + delta + colors.length) % colors.length];
    onChange(next);
    // Focus goes with the choice, as in a native radio group.
    swatches.current.get(next)?.focus();
  };

  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="flex flex-wrap gap-2">
      {colors.map((color) => {
        const checked = color === value;
        const name = colorName(color);
        return (
          <button
            key={color}
            ref={(el) => {
              if (el) swatches.current.set(color, el);
              else swatches.current.delete(color);
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={name}
            title={name}
            tabIndex={color === stop ? 0 : -1}
            onClick={() => onChange(color)}
            onKeyDown={onKeyDown}
            style={{ backgroundColor: color }}
            className={cn(
              'focus-ring flex h-6 w-6 shrink-0 items-center justify-center rounded-full forced-colors:[forced-color-adjust:none]',
              checked && 'ring-2 ring-action ring-offset-2 ring-offset-card',
            )}
          >
            {checked && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} aria-hidden />}
          </button>
        );
      })}
    </div>
  );
}
